import { useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import {
	buildSDPSearchPayload,
	getLastSearchData,
	getTravelLocationDataFromQuery,
	normalizeDate,
	pickInitialValue,
	hasCustomBookingOccupancies,
} from "app/utils/smartDPUtils";
import {
	QUOTATION_CODE_STATUS,
	RESOLUTION,
	SDP_PRODUCTS_FILTERS_KEY,
	STOPOVER_DIRECTIONS,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import { unsetSDPProductsFilter } from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import PropTypes from "prop-types";
import get from "lodash/get";
import { getAvailableStopoverCities, getCabinFallback } from "app/pages/SmartDP/smartDPSelectors";
import { bindActionCreators } from "redux";
import { useIntl } from "react-intl";
import StopoverSearchForm from "app/pages/Stopover/StopoverSearchForm/StopoverSearchForm";
import { fetchAllStopoverCities } from "app/pages/Stopover/stopoverActionCreators";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import withRouter from "app/utils/hocs/withRouter";
import { useLoadingBar } from "app/features/loadingBar";

const StopoverSearchFormContainer = ownProps => {
	const stateProps = useSelector(state => mapStateToProps(state, ownProps), shallowEqual);
	const dispatch = useDispatch();
	const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);
	const props = { ...ownProps, ...stateProps, ...dispatchProps };

	const intl = useIntl();
	const {
		unsetSDPProductsFilter,
		onSubmit,
		initialValues: propsInitialValues,
		initialStopoverDirectionCode,
	} = props;

	const navigate = useNavigate();
	const { pulse } = useLoadingBar();

	const stopoverDirections = useMemo(
		() => [
			{
				label: intl.formatMessage({ id: "sdp.search.stopover.outbound.label" }),
				code: STOPOVER_DIRECTIONS.OUTBOUND,
			},
			{
				label: intl.formatMessage({ id: "sdp.search.stopover.inbound.label" }),
				code: STOPOVER_DIRECTIONS.INBOUND,
			},
		],
		[intl]
	);

	const initialValues = useMemo(
		() => ({
			...propsInitialValues,
			stopoverDirection:
				stopoverDirections.find(
					d => String(d.code) === String(initialStopoverDirectionCode)
				) || stopoverDirections[0],
		}),
		[propsInitialValues, stopoverDirections, initialStopoverDirectionCode]
	);

	const handleFormSubmission = useCallback(({ values, pathname = "" }) => {
		const queryParams = buildSDPSearchPayload(values);
		pulse();
		unsetSDPProductsFilter(SDP_PRODUCTS_FILTERS_KEY.BUDGET);

		if (onSubmit) {
			onSubmit(values);
		}

		if (pathname.includes("/stopover/listing")) {
			navigate({ pathname: pathname, search: queryParams }, { replace: true });
		} else {
			navigate({ pathname: "/stopover/listing", search: queryParams });
		}
	}, []);

	return (
		<div className="search-form__container">
			<StopoverSearchForm
				{...props}
				initialValues={initialValues}
				stopoverDirections={stopoverDirections}
				onSuccess={handleFormSubmission}
			/>
		</div>
	);
};

StopoverSearchFormContainer.propTypes = {
	unsetSDPProductsFilter: PropTypes.func,
	fetchAllStopoverCities: PropTypes.func,
	isMobile: PropTypes.bool,
	brand: PropTypes.string,
	cabinFallback: PropTypes.object,
	initialFocusField: PropTypes.string,
	disableFormPersistance: PropTypes.bool, // utilisé pour éviter le flicker des champs entre la page merch et la home SDP
	onSubmit: PropTypes.func,
	initialValues: PropTypes.object,
	initialStopoverDirectionCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const mapStateToProps = (state, props) => {
	const { booking } = state;
	const { disableFormPersistance } = props;
	const isMerchPage = props?.location?.pathname?.includes("/merch");
	const lastSearch = getLastSearchData(state, WEBSITE_TUNNEL_TYPES.STOPOVER);

	const initialValues = {
		travelDates: {
			departureDate: "",
			endDate: "",
		},
	};

	if (get(state, "smartDP.cabins[0]")) {
		initialValues.cabin = get(state, "smartDP.cabins[0]");
	}

	const destinations = state.smartDP?.destinations;
	const departureCities = state.smartDP?.departureCities;

	const getLocationsFrom = query =>
		getTravelLocationDataFromQuery({
			queryParams: query,
			destinations,
			departures: departureCities,
		});

	const { departure: bookingDeparture, destination: bookingDestination } = getLocationsFrom({
		departureCityCode: booking?.departureCity?.code,
		destinationResortCode: booking?.destinationResort?.code,
	});

	const { departure: lastDeparture, destination: lastDestination } = getLocationsFrom({
		departureCityCode: lastSearch?.departureCity?.code,
		destinationResortCode: lastSearch?.destinationResort?.code,
	});

	// En merch, la page fixe destination/dates mais on garde l'historique pour le reste.
	const canUseLastSearchForMainFields = !disableFormPersistance && !isMerchPage;
	const canUseLastSearchForOtherFields = !disableFormPersistance;

	// Centralise la logique de priorité booking -> dernière recherche pour limiter le bruit.
	const pickValue = params =>
		pickInitialValue({
			disableFormPersistance,
			...params,
		});

	const normalizeValue = date => (date ? normalizeDate(date) : undefined);

	// 1. Destination/départ : on pioche dans la persistance puis dans la dernière recherche.
	const destinationResort = pickValue({
		bookingValue: bookingDestination,
		lastSearchValue: lastDestination,
		allowLastSearch: canUseLastSearchForMainFields,
		isValueValid: value => !isEmpty(value),
	});

	const hasDestination = !isEmpty(destinationResort);
	if (hasDestination) {
		initialValues.destinationResort = destinationResort;

		const departureCity = pickValue({
			bookingValue: bookingDeparture,
			lastSearchValue: lastDeparture,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => !isEmpty(value),
		});

		if (departureCity) {
			initialValues.departureCity = departureCity;
		}
	}

	// 2. Cabine, dates et durée suivent la même logique de persistance.
	if (!disableFormPersistance) {
		const bookingCabin =
			Boolean(booking?.cabin?.code) && !isEmpty(booking?.cabin) ? booking?.cabin : undefined;
		const lastSearchCabin =
			Boolean(lastSearch?.cabin?.code) && !isEmpty(lastSearch?.cabin)
				? lastSearch?.cabin
				: undefined;

		const persistedCabin = pickValue({
			bookingValue: bookingCabin,
			lastSearchValue: lastSearchCabin,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => !isEmpty(value) && Boolean(value?.code),
		});

		if (persistedCabin) {
			initialValues.cabin = persistedCabin;
		}
	}

	if (hasDestination && !disableFormPersistance) {
		const departureDate = pickValue({
			bookingValue: normalizeValue(booking?.departureDate),
			lastSearchValue: normalizeValue(lastSearch?.departureDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (departureDate) {
			initialValues.travelDates.departureDate = departureDate;
		}

		const endDate = pickValue({
			bookingValue: normalizeValue(booking?.endDate),
			lastSearchValue: normalizeValue(lastSearch?.endDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (endDate) {
			initialValues.travelDates.endDate = endDate;
		}

		const bookingDuration = booking?.duration?.value;
		const lastDuration = lastSearch?.duration?.value;

		const durationCode = pickValue({
			bookingValue: bookingDuration,
			lastSearchValue: lastDuration,
			allowLastSearch: canUseLastSearchForMainFields,
			isValueValid: value => !isNil(value),
		});

		if (!isNil(durationCode)) {
			initialValues.duration = { code: durationCode };
		}
	}

	// 3. On garde les occupancies seulement si l'utilisateur a réellement changé la config par défaut.
	if (!disableFormPersistance) {
		const bookingOccupancies = hasCustomBookingOccupancies(booking)
			? booking?.occupancies
			: undefined;
		const lastSearchOccupancies =
			lastSearch?.occupancies?.length > 0 ? lastSearch?.occupancies : undefined;

		const occupancies = pickValue({
			bookingValue: bookingOccupancies,
			lastSearchValue: lastSearchOccupancies,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => Array.isArray(value) && value.length > 0,
		});

		if (occupancies) {
			initialValues.occupancies = occupancies;
		}
	}

	const stopoverCities = props.stopoverCities || getAvailableStopoverCities(state);

	const findStopoverCity = code =>
		stopoverCities?.find(city => String(city?.code) === String(code));

	const stopoverCityCandidates = [
		!disableFormPersistance ? findStopoverCity(booking?.stopoverCityId) : undefined,
		!disableFormPersistance ? findStopoverCity(lastSearch?.stopoverCityId) : undefined,
		stopoverCities?.[0],
	];

	const selectedStopoverCity = stopoverCityCandidates.find(
		candidate => candidate !== null && candidate !== undefined
	);

	if (selectedStopoverCity) {
		initialValues.stopoverCity = {
			code: selectedStopoverCity.code,
			label: selectedStopoverCity.label,
		};
	}

	// Après un clearBooking on repart en outbound; on ne persiste inbound que si l'utilisateur l'avait choisi.
	const stopoverDirectionCodeCandidates = [
		!disableFormPersistance && booking?.stopoverDirection !== STOPOVER_DIRECTIONS.OUTBOUND
			? booking?.stopoverDirection
			: undefined,
		!disableFormPersistance ? lastSearch?.stopoverDirection : undefined,
		STOPOVER_DIRECTIONS.OUTBOUND,
	];

	const initialStopoverDirectionCode =
		stopoverDirectionCodeCandidates.find(
			candidate => candidate !== null && candidate !== undefined
		) || STOPOVER_DIRECTIONS.OUTBOUND;

	return {
		initialValues,
		destinations,
		departureCities,
		stopoverCities,
		initialStopoverDirectionCode,
		cabins: state.smartDP.cabins,
		validateOnMount: state.smartDP.status === QUOTATION_CODE_STATUS.BAD_REQUEST,
		isMobile: [RESOLUTION.SMALL, RESOLUTION.MEDIUM].includes(state?.resolution),
		brand: state.brand.code,
		cabinFallback: getCabinFallback(state),
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators({ unsetSDPProductsFilter, fetchAllStopoverCities }, dispatch);
};

export default withRouter(StopoverSearchFormContainer);
